import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for AI hint functionality with rate limiting
 * Uses Google Gemini API with daily usage limits to control costs
 */

const DAILY_LIMIT = 10;
const STORAGE_KEY = 'test_detective_ai_usage';

export const useAIHints = () => {
  const [usage, setUsage] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        // Reset if new day
        if (data.date !== new Date().toDateString()) {
          return { count: 0, date: new Date().toDateString() };
        }
        return data;
      }
    } catch (e) {
      console.error('Error reading AI usage from storage:', e);
    }
    return { count: 0, date: new Date().toDateString() };
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Save usage to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
    } catch (e) {
      console.error('Error saving AI usage to storage:', e);
    }
  }, [usage]);

  const canUseAI = usage.count < DAILY_LIMIT;
  const usesRemaining = DAILY_LIMIT - usage.count;

  // Calculate time until reset (midnight)
  const getTimeUntilReset = useCallback(() => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const diff = midnight - now;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  }, []);

  const getHint = useCallback(async (challenge, userContext = '') => {
    if (!canUseAI) {
      return { 
        success: false, 
        error: `Daily limit reached (${DAILY_LIMIT} hints). Resets in ${getTimeUntilReset()}.`
      };
    }

    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    
    if (!apiKey) {
      return { 
        success: false, 
        error: 'AI features not configured. API key missing.' 
      };
    }

    setIsLoading(true);
    setError(null);

    try {
      const prompt = buildHintPrompt(challenge, userContext);
      
      // Using gemini-2.0-flash (stable model as of Jan 2026)
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: {
              temperature: 0.7,
              maxOutputTokens: 200,
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      const hint = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!hint) {
        throw new Error('No hint generated');
      }

      // Increment usage count
      setUsage(prev => ({
        count: prev.count + 1,
        date: prev.date
      }));

      setIsLoading(false);
      return { success: true, hint };

    } catch (err) {
      setIsLoading(false);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [canUseAI, getTimeUntilReset]);

  return {
    canUseAI,
    usesRemaining,
    isLoading,
    error,
    getHint,
    getTimeUntilReset,
    dailyLimit: DAILY_LIMIT
  };
};

/**
 * Build a prompt for the AI to generate a helpful hint
 */
function buildHintPrompt(challenge, userContext) {
  return `You are a senior QA engineer helping someone learn software testing concepts.

CHALLENGE TYPE: ${challenge.type}
CHALLENGE TITLE: ${challenge.title}
SETUP: ${challenge.setup}
${challenge.code ? `CODE:\n${challenge.code}` : ''}
${challenge.testCases ? `EXISTING TESTS:\n${challenge.testCases.join('\n')}` : ''}
${challenge.requirements ? `REQUIREMENTS:\n${challenge.requirements}` : ''}
QUESTION: ${challenge.question}

OPTIONS:
${challenge.options.map((opt, i) => `${String.fromCharCode(65 + i)}. ${opt}`).join('\n')}

Give a SHORT hint (2-3 sentences max) that guides the learner toward the right answer WITHOUT giving it away directly. 
- Don't say which option is correct
- Focus on the key concept they should think about
- Be encouraging and educational
- Sound like a mentor, not a textbook`;
}